import React, { FC, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/system";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { COLORS, FONT_FAMILIES, FONT_WEIGHTS, GAPS, RADII, SPACINGS, getColors } from "../../common/consts";
import { useTheme } from "../../common/contexts/ThemeContext";
import Text, { TextVariant } from "../Display/Text";
import DataGrid, { DataGridCol } from "../Display/DataGrid";
import { ChartSkeleton, StatBones, TableSkeleton, TextBone } from "../Display/Skeletons";
import { getUsageReport } from "../../services/firebase";
import { UsageReport } from "../../types/addons";
import { HUBSPOT_TILE } from "../HubSpotUi/theme";

const Card = styled("div")`
  border-radius: ${RADII.md};
  box-shadow: rgba(69, 66, 98, 0.16) 0 0 1px, rgba(69, 66, 98, 0.16) 0 1px 2px;
  background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
  padding: ${SPACINGS.xl};
  max-width: 900px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${GAPS.md};
  ${HUBSPOT_TILE}
`;

const Title = styled("div")`
  font-family: ${FONT_FAMILIES.titles};
  font-weight: ${FONT_WEIGHTS.semibold};
  font-size: 1rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const Muted = styled(Text)`
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  margin: 0;
`;

const Totals = styled("div")`
  display: flex;
  gap: ${SPACINGS.xxl};
  flex-wrap: wrap;
`;

const Stat = styled("div")`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled("div")`
  font-family: ${FONT_FAMILIES.titles};
  font-size: 1.5rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  line-height: 2.25rem;
  color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
`;

const StatLabel = styled("div")`
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
`;

const usd = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v || 0);
const monthName = (period: string) => {
  const [y, m] = period.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

interface Row {
  id: string;
  month: string;
  lookups: number;
  found: number;
  verifications: number;
  charge: number;
}

const columns: DataGridCol<Row>[] = [
  { field: "month", headerName: "Month", flex: 1.2, minWidth: 130 },
  { field: "lookups", headerName: "Phone lookups", flex: 1, minWidth: 130, type: "number" },
  { field: "found", headerName: "Numbers found", flex: 1, minWidth: 130, type: "number" },
  { field: "verifications", headerName: "Verifications", flex: 1, minWidth: 120, type: "number" },
  { field: "charge", headerName: "Charge", flex: 1, minWidth: 110, type: "number", valueFormatter: (v: number) => usd(v) },
];

/**
 * What metered usage costs. Phone lookups, verifications and caller ID checks
 * are billed on what the providers charged, so this is the only place a
 * customer can see the bill building up before the invoice arrives.
 */
/**
 * The usage card as it arrives: the heading, the four totals, the monthly
 * bar chart and the month-by-month table underneath.
 */
export const MeteredUsageSectionSkeleton: FC = () => (
  <Card>
    <div>
      <TextBone width={160} size="1rem" />
      <TextBone width="94%" size="0.875rem" />
      <TextBone width="62%" size="0.875rem" />
    </div>
    <Totals>
      <StatBones valueWidth={96} labelWidth={128} />
      <StatBones valueWidth={88} labelWidth={104} />
      <StatBones valueWidth={72} labelWidth={110} />
      <StatBones valueWidth={64} labelWidth={96} />
    </Totals>
    <div style={{ height: 220 }}>
      <ChartSkeleton height={220} series={1} ticks={8} />
    </div>
    <TableSkeleton
      columns={columns.map((column) => ({
        flex: column.flex,
        minWidth: column.minWidth,
        headerName: column.headerName,
      }))}
      rows={6}
      rowHeight={52}
      headerHeight={48}
    />
  </Card>
);

const MeteredUsageSection: FC = () => {
  const { mode } = useTheme();
  const colors = getColors(mode);
  const [report, setReport] = useState<UsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getUsageReport(12)
      .then(setReport)
      .catch(() => setError("Could not load metered usage."));
  }, []);

  const rows: Row[] = useMemo(
    () =>
      (report?.months || [])
        .map((m) => ({
          id: m.period,
          month: monthName(m.period),
          lookups: m.phoneLookups,
          found: m.phoneLookupsFound,
          verifications: m.phoneVerifications,
          charge: m.phoneBillableUsd ?? 0,
        }))
        .reverse(),
    [report]
  );

  const chartData = useMemo(
    () => (report?.months || []).map((m) => ({ month: monthName(m.period), charge: Number((m.phoneBillableUsd ?? 0).toFixed(2)) })),
    [report]
  );

  if (!report && error) return <Muted variant={TextVariant.sm}>{error}</Muted>;

  if (!report) return <MeteredUsageSectionSkeleton />;

  const totals = report.totals;
  const anyUsage = rows.some((r) => r.lookups || r.verifications || r.charge);
  const thisMonth = rows[0];

  return (
    <Card>
      <div>
        <Title>Metered usage</Title>
        <Muted variant={TextVariant.sm}>
          Phone lookups, phone verifications and caller ID checks are billed on what the providers charge us, plus the card fee. Nothing else here is metered.
        </Muted>
      </div>

      {!anyUsage ? (
        <Muted variant={TextVariant.sm}>No metered usage yet. Charges appear here as soon as a lookup runs.</Muted>
      ) : (
        <>
          <Totals>
            <Stat>
              <StatValue>{usd(thisMonth?.charge || 0)}</StatValue>
              <StatLabel>{thisMonth?.month || "This month"} so far</StatLabel>
            </Stat>
            <Stat>
              <StatValue>{usd(totals.phoneBillableUsd ?? 0)}</StatValue>
              <StatLabel>Last 12 months</StatLabel>
            </Stat>
            <Stat>
              <StatValue>{totals.phoneLookups.toLocaleString()}</StatValue>
              <StatLabel>Phone lookups</StatLabel>
            </Stat>
            <Stat>
              <StatValue>{totals.phoneVerifications.toLocaleString()}</StatValue>
              <StatLabel>Verifications</StatLabel>
            </Stat>
          </Totals>

          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="transparent" />
                <XAxis
                  dataKey="month"
                  tickLine={false}
                  axisLine={{ stroke: colors.chartAxisLine }}
                  tick={{ fill: colors.chartAxisColor, fontSize: "0.875rem", fontWeight: 400, fontFamily: FONT_FAMILIES.regular }}
                />
                <YAxis
                  width={64}
                  tickLine={false}
                  axisLine={{ stroke: colors.chartAxisLine }}
                  tick={{ fill: colors.chartAxisColor, fontSize: "0.875rem", fontWeight: 400, fontFamily: FONT_FAMILIES.regular }}
                  tickFormatter={(v: number) => usd(v)}
                />
                <RechartsTooltip formatter={(v: number) => usd(v)} />
                <Legend />
                <Bar dataKey="charge" name="Metered charge" fill={COLORS.chartEmailEnriched} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            disableColumnMenu
            hideFooter={rows.length <= 12}
            initialState={{ pagination: { paginationModel: { pageSize: 12 } } }}
          />
        </>
      )}
    </Card>
  );
};

export default MeteredUsageSection;
